/*
 * Figma `119:174` — SELL-04 · Sales Channels · Attribution + Report.
 *
 * `TableTemplate`: the totals, the channel table, and an insight rail beside it. The frame's own
 * annotation calls for "actionable insight" rather than a table of numbers, and the actionable
 * thing on this screen is always the same question — how much of the revenue can be credited to
 * anything at all.
 *
 * The report carries one row per channel plus a row with no `channelId`: the traffic that arrived
 * without a link. An event whose revenue is mostly that row is an event whose marketing spend
 * cannot be judged, and the rail says so as a sentence with a number rather than leaving it to be
 * inferred from a table.
 *
 * `clicks: null` is rendered as an em dash, never as zero, and no conversion rate is computed from
 * it. The DTO is explicit that null means never measured, and 0% would be a measurement nobody
 * took — on the screen an organizer uses to decide where to spend next.
 *
 * Deviation from the frame: it draws three tabs, the third being ตัวแทนจำหน่าย. Resellers are a
 * different screen with its own report in this codebase, so that tab is not invented here.
 */
import {
  Alert, Button, Card, KpiCard, KpiGrid, ManagementTemplate, MeterRow, Select, StatusBadge,
  TableTemplate, TextField,
} from '..';
import type { BadgeTone, Column } from '..';
import { useState } from 'react';
import type { Destination } from '../destinations';
import type { SalesChannelPlatform, SalesChannelReportDto } from '../../api';
import type { SalesChannelsState } from './useSalesChannels';
import { PLATFORM_LABEL, useSalesChannels } from './useSalesChannels';

type Tab = 'overview' | 'links';

const baht = (n: number) => `฿${Math.round(n).toLocaleString('th-TH')}`;
const count = (n: number | null) => (n === null ? '—' : n.toLocaleString('th-TH'));

/** What the row can be said to be, from what was actually measured. */
function rowState(row: SalesChannelReportDto): { label: string; tone: BadgeTone } {
  if (row.channelId === null) return { label: 'ไม่ได้ติดตาม', tone: 'neutral' };
  if (row.clicks === null) return { label: 'ยังไม่วัดคลิก', tone: 'warning' };
  if (row.clicks === 0 && row.orders === 0) return { label: 'ยังไม่มีทราฟฟิก', tone: 'neutral' };
  return { label: 'ใช้งาน', tone: 'success' };
}

export function SalesChannelsScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: SalesChannelsState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    channels, report, attribution, days, setDays, loading, loadError, busy, message,
    draft, setDraft, create, archive, setLineRoute, linkFor,
  } = state;
  const [tab, setTab] = useState<Tab>('overview');

  const active = channels.filter((c) => !(c.archivedAt ?? c.archived_at)).length;
  const clicks = report.reduce((sum, r) => sum + (r.clicks ?? 0), 0);
  const orders = report.reduce((sum, r) => sum + r.orders, 0);
  const anyClicksMeasured = report.some((r) => r.clicks !== null);

  const columns: Column<SalesChannelReportDto>[] = [
    {
      key: 'label',
      header: 'ช่องทาง',
      cell: (r) => (
        <span className="jt-cell-stack">
          <span>{r.label}</span>
          <span className="jt-cell-sub jt-mono">{r.code ?? '—'}{r.platform ? ` · ${r.platform}` : ''}</span>
        </span>
      ),
    },
    { key: 'clicks', header: 'คลิก', align: 'end', width: '110px', cell: (r) => <span className="jt-mono">{count(r.clicks)}</span> },
    { key: 'orders', header: 'คำสั่งซื้อ', align: 'end', width: '110px', cell: (r) => <span className="jt-mono">{r.orders.toLocaleString('th-TH')}</span> },
    { key: 'paid', header: 'ชำระแล้ว', align: 'end', width: '110px', cell: (r) => <span className="jt-mono">{r.paidOrders.toLocaleString('th-TH')}</span> },
    { key: 'revenue', header: 'รายได้', align: 'end', width: '140px', cell: (r) => <span className="jt-mono">{baht(r.revenue)}</span> },
    {
      key: 'state',
      header: 'สถานะ',
      width: '150px',
      cell: (r) => {
        const s = rowState(r);
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
  ];

  const kpis = (
    <>
      {[
        <KpiCard key="ch" label="ช่องทางที่ใช้งาน" value={String(active)} meta={`ทั้งหมด ${channels.length}`} />,
        <KpiCard
          key="clicks"
          label="คลิกทั้งหมด"
          value={anyClicksMeasured ? clicks.toLocaleString('th-TH') : '—'}
          meta={anyClicksMeasured ? `ย้อนหลัง ${days} วัน` : 'ยังไม่มีช่องทางที่วัดคลิกได้'}
        />,
        <KpiCard key="orders" label="คำสั่งซื้อ" value={orders.toLocaleString('th-TH')} />,
        <KpiCard
          key="rev"
          label="รายได้ที่ติดตามได้"
          value={baht(attribution.attributedRevenue)}
          meta={attribution.attributedShare === null
            ? 'ยังไม่มีรายได้'
            : `${attribution.attributedShare.toFixed(0)}% ของรายได้ทั้งหมด`}
          tone={attribution.attributedShare !== null && attribution.attributedShare < 50 ? 'warning' : undefined}
        />,
      ]}
    </>
  );

  const toolbar = (
    <>
          <div className="jt-inline-actions" role="tablist" aria-label="มุมมองช่องทางการขาย">
            {([['overview', 'ภาพรวมช่องทาง'], ['links', 'ลิงก์แคมเปญ']] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className="jt-phase-tab"
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <Select
            label="ช่วงเวลา"
            value={String(days)}
            options={[7, 14, 30, 90].map((d) => ({ value: String(d), label: `ย้อนหลัง ${d} วัน` }))}
            onChange={(e) => setDays(Number(e.target.value))}
          />
    </>
  );

  /*
   * Two shapes behind two tabs, and two templates rather than one with an empty table in it. The
   * overview is a report; the links tab is a registry beside a form, and drawing an empty table
   * above it to keep a single template would be a box that says nothing.
   */
  const linksView = (
          <div className="jt-registry-split--insight">
            <Card title={`ลิงก์ช่องทาง · ${channels.length}`} sub="โพสต์ลิงก์นี้แทนลิงก์งานปกติ เพื่อให้ยอดขายถูกนับเข้าช่องทางนั้น">
              {channels.length === 0 && <p className="jt-field-hint">ยังไม่มีลิงก์ช่องทาง — สร้างทางขวา</p>}
              <ul className="jt-account-list">
                {channels.map((channel) => {
                  const archived = !!(channel.archivedAt ?? channel.archived_at);
                  return (
                    <li key={channel.id} className="jt-band-row">
                      <div className="jt-band-copy">
                        <span className="jt-band-name">
                          {channel.label} · {PLATFORM_LABEL[channel.platform]}
                        </span>
                        <span className="jt-band-usage jt-mono">{linkFor(channel)}</span>
                      </div>
                      <div className="jt-inline-actions">
                        {archived
                          ? <StatusBadge tone="neutral">เก็บแล้ว</StatusBadge>
                          : <StatusBadge tone="success">ใช้งาน</StatusBadge>}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void navigator.clipboard?.writeText(linkFor(channel))}
                        >
                          คัดลอกลิงก์
                        </Button>
                        {!archived && (
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void archive(channel.id)}>
                            เก็บ
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>

            <Card title="สร้างลิงก์ช่องทาง" sub="ใช้โค้ดสั้น ๆ ที่จำได้ เช่น ig-teaser หรือ partner-north">
              <TextField
                label="โค้ดช่องทาง"
                requiredMark
                value={draft.code}
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                hint="ปรากฏในลิงก์ที่โพสต์ออกไป จึงเปลี่ยนภายหลังไม่ได้"
              />
              <TextField
                label="ชื่อที่ใช้เรียกในรายงาน"
                requiredMark
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
              <Select
                label="แพลตฟอร์ม"
                value={draft.platform}
                options={(Object.keys(PLATFORM_LABEL) as SalesChannelPlatform[])
                  .map((p) => ({ value: p, label: PLATFORM_LABEL[p] }))}
                onChange={(e) => setDraft({ ...draft, platform: e.target.value as SalesChannelPlatform })}
              />
              {/*
                * One choice of three rather than two switches. A link that sends people into the
                * OA and also lives inside it is a loop the server refuses — a radio group makes
                * that a shape the form cannot express, instead of an error met after pressing
                * สร้าง.
                */}
              <Select
                label="เส้นทางผ่าน LINE"
                value={draft.lineRoute}
                options={[
                  { value: 'none', label: 'ไปหน้างานโดยตรง' },
                  { value: 'into_oa', label: 'แวะเข้า LINE OA ก่อน' },
                  { value: 'inside_oa', label: 'ลิงก์นี้อยู่ใน LINE OA อยู่แล้ว' },
                ]}
                onChange={(e) => setDraft({ ...draft, lineRoute: e.target.value as typeof draft.lineRoute })}
              />
              <div className="jt-inline-actions">
                <Button
                  variant="primary"
                  disabled={busy || !draft.code.trim() || !draft.label.trim()}
                  onClick={() => void create()}
                >
                  {busy ? 'กำลังสร้าง…' : 'สร้างลิงก์'}
                </Button>
              </div>
            </Card>
          </div>
  );

  if (tab === 'links') {
    return (
      <ManagementTemplate
        gate={{ destination, loading, error: loadError || null, permitted, role }}
        summary={<><KpiGrid>{kpis}</KpiGrid>{message && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{message}</Alert>}</>}
        toolbar={toolbar}
        items={linksView}
      />
    );
  }

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      kpis={kpis}
      toolbar={toolbar}
      table={{
        label: 'ผลของแต่ละช่องทาง',
        columns,
        rows: report,
        rowKey: (r) => r.channelId ?? `unattributed-${r.label}`,
        emptyTitle: 'ยังไม่มีข้อมูลช่องทาง',
        emptyText: 'สร้างลิงก์ช่องทางแล้วโพสต์ไป ระบบจะเริ่มนับคลิกและยอดขายที่มาจากลิงก์นั้น',
      }}
      drawer={
        <>
          {message && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{message}</Alert>}

          <div className="jt-registry-split--insight">
            <Card title="ส่วนแบ่งรายได้ตามช่องทาง" sub={`รวมทุกช่องทาง ${baht(attribution.totalRevenue)}`}>
              {attribution.breakdown.length === 0
                ? <p className="jt-field-hint">ยังไม่มีรายได้ให้แบ่งตามช่องทาง</p>
                : attribution.breakdown.slice(0, 6).map((row) => (
                  <MeterRow
                    key={row.label}
                    label={row.label}
                    value={baht(row.revenue)}
                    percent={row.percent}
                    caption={`${row.percent.toFixed(0)}% ของรายได้ทั้งหมด`}
                  />
                ))}
            </Card>

            <Card title="สิ่งที่ควรทำต่อ">
              {attribution.attributedShare === null ? (
                <p className="jt-field-hint">ยังไม่มีรายได้เข้ามา — เริ่มจากสร้างลิงก์ให้ช่องทางที่กำลังโปรโมตอยู่</p>
              ) : attribution.attributedShare < 50 ? (
                <Alert tone="warning" title={`${(100 - attribution.attributedShare).toFixed(0)}% ของรายได้ยังไม่รู้ว่ามาจากไหน`}>
                  {baht(attribution.unattributedRevenue)} เข้ามาโดยไม่ผ่านลิงก์ช่องทางใด — ตราบใดที่สัดส่วนนี้ยังสูง
                  ตัวเลขของแต่ละช่องทางจะบอกไม่ได้ว่าที่ไหนคุ้มกว่ากัน ลองใช้ลิงก์เฉพาะช่องทางในโพสต์ถัดไป
                </Alert>
              ) : (
                <Alert tone="success" title={`ติดตามได้ ${attribution.attributedShare.toFixed(0)}% ของรายได้`}>
                  สัดส่วนนี้พอให้เปรียบเทียบช่องทางกันได้ — ช่องทางที่ทำรายได้สูงสุดตอนนี้คือ
                  {' '}{attribution.breakdown[0]?.label ?? '—'}
                </Alert>
              )}
              {!anyClicksMeasured && channels.length > 0 && (
                <p className="jt-field-hint">
                  ยังไม่มีช่องทางใดวัดคลิกได้ — ตัวเลขคลิกจะขึ้นเมื่อมีคนกดลิงก์ที่สร้างจากหน้านี้
                </p>
              )}
            </Card>
          </div>

        </>
      }
    />
  );
}

export function SalesChannelsDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useSalesChannels(token, eventSlug);
  return <SalesChannelsScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
